import { Alert } from '@mui/material'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useActionState, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import {
  AdminUpdateQcTestRequest,
  ScoreParameterDto,
  adminUpdateTestDefinition,
  fetchQualityTestById,
  fetchScoreCalculatorTypes,
  fetchTestParameters,
  fetchVersionHistory,
} from '../api/services/qualityControlService'
import { QcTestDefinition, QcTestDefinitionFormValues } from '../types'
import QualityTestDefinitionForm from './components/QualityTestDefinitionForm'
import { useCurrentSite } from '../store/store.hooks'

// Extend the type to handle optional admin fields the backend may return
// that are not yet surfaced in the shared QualityTestDefinition TS type.
type TestDefinitionWithAdminFields = QcTestDefinition & {
  scoreCalculatorType?: string
  scheduledActivationDate?: string
}

const QualityTestEditor = () => {
  const { t } = useTranslation()
  const { testDefinitionId } = useParams<{ testDefinitionId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const currentSite = useCurrentSite()

  // 1. Fetch version history to find the active version's id
  const { data: versions } = useSuspenseQuery({
    queryKey: ['quality-tests', testDefinitionId, 'versions'],
    queryFn: () => fetchVersionHistory(testDefinitionId!, currentSite?.id ?? ''),
  })

  const now = new Date()
  const activeVersion = versions.find((v) => v.isCurrentlyActive)
  const pendingVersion = versions.find((v) => !v.isCurrentlyActive && new Date(v.scheduledActivationDate) > now)
  const editingVersion = pendingVersion ?? activeVersion
  const activeTestId = editingVersion?.id ?? ''
  const canSchedule = activeVersion != null

  // 2. Fetch the active test definition
  const { data: testDefinitionRaw } = useSuspenseQuery({
    queryKey: ['quality-tests', activeTestId],
    queryFn: () => fetchQualityTestById(currentSite?.id ?? '', activeTestId),
  })

  const testDefinition = testDefinitionRaw as TestDefinitionWithAdminFields

  // 3. Fetch score parameters for this test group
  const { data: parametersData } = useSuspenseQuery({
    queryKey: ['quality-tests', 'parameters', testDefinitionId, editingVersion?.id],
    queryFn: () => fetchTestParameters(testDefinitionId!, currentSite?.id ?? '', editingVersion?.id),
  })

  // 4. Fetch available score calculator types
  const { data: scoreCalculatorTypes } = useSuspenseQuery({
    queryKey: ['quality-tests', 'score-calculator-types'],
    queryFn: () => fetchScoreCalculatorTypes(currentSite?.id ?? ''),
  })

  const [scoreParameters, setScoreParameters] = useState<ScoreParameterDto[]>(parametersData)

  // Sync local edit state only when the version being edited changes, not on every
  // background cache update (which would clobber in-progress edits).
  useEffect(() => {
    setScoreParameters(parametersData)
  }, [activeTestId])

  const pendingBannerDate = pendingVersion?.scheduledActivationDate
    ? new Date(pendingVersion.scheduledActivationDate).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  const initialScheduledDate = (() => {
    if (!testDefinition.scheduledActivationDate) return ''
    const d = new Date(testDefinition.scheduledActivationDate)
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
  })()

  const defaultValues: QcTestDefinitionFormValues = {
    displayName: testDefinition.displayName,
    category: testDefinition.category ?? '',
    description: testDefinition.description ?? '',
    displayOrder: String(testDefinition.displayOrder ?? ''),
    canTestMultipleEntities: testDefinition.canTestMultipleEntities,
    scoreCalculatorType: testDefinition.scoreCalculatorType ?? '',
    formJson: JSON.stringify(testDefinition.form, null, 2),
    scheduledActivationDate: initialScheduledDate,
    targetTestsPerShift: String(testDefinition.targetTestsPerShift ?? ''),
  }

  const [actionError, submitAction, isPending] = useActionState(
    async (
      _prevState: string | null,
      values: QcTestDefinitionFormValues & { scoreParameters: ScoreParameterDto[] },
    ): Promise<string | null> => {
      const nowAtSubmit = new Date()
      const nowLocalAtSubmit = new Date(nowAtSubmit.getTime() - nowAtSubmit.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16)
      if (values.scheduledActivationDate && values.scheduledActivationDate < nowLocalAtSubmit) {
        return t('qualityControl.scheduledDateInPastError')
      }
      try {
        const payload: AdminUpdateQcTestRequest = {
          displayName: values.displayName,
          category: values.category || undefined,
          description: values.description || undefined,
          displayOrder:
            values.displayOrder && Number(values.displayOrder) >= 0 ? Number(values.displayOrder) : undefined,
          form: values.formJson,
          canTestMultipleEntities: values.canTestMultipleEntities,
          scoreCalculatorType: values.scoreCalculatorType,
          scoreParameters: values.scoreParameters,
          scheduledActivationDate: values.scheduledActivationDate
            ? new Date(values.scheduledActivationDate).toISOString()
            : undefined,
          targetTestsPerShift: values.targetTestsPerShift ? Number(values.targetTestsPerShift) : undefined,
        }
        await adminUpdateTestDefinition(testDefinitionId!, currentSite?.id ?? '', payload)
        await queryClient.invalidateQueries({ queryKey: ['quality-tests', testDefinitionId] })
        await queryClient.invalidateQueries({ queryKey: ['quality-tests', activeTestId] })
        await queryClient.invalidateQueries({ queryKey: ['quality-tests', 'parameters', testDefinitionId] })
        await queryClient.invalidateQueries({ queryKey: ['quality-tests', 'list'] })
        navigate('/quality-control')
        return null
      } catch (err) {
        const axiosData = (err as { response?: { data?: { detail?: string; title?: string } } })?.response?.data
        if (axiosData?.detail) return axiosData.detail
        if (axiosData?.title) return axiosData.title
        return err instanceof Error ? err.message : 'An error occurred while saving the test definition.'
      }
    },
    null,
  )

  const handleSubmit = (values: QcTestDefinitionFormValues & { scoreParameters: ScoreParameterDto[] }) => {
    setScoreParameters(values.scoreParameters)
    submitAction(values)
  }

  return (
    <QualityTestDefinitionForm
      defaultValues={defaultValues}
      scoreCalculatorTypes={scoreCalculatorTypes}
      onSubmit={handleSubmit}
      isSubmitting={isPending}
      canSchedule={canSchedule}
      version={editingVersion?.version}
      scoreParametersProps={{ parameters: scoreParameters, onChange: setScoreParameters }}
      pendingVersionBanner={
        pendingVersion ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t('qualityControl.pendingVersionBanner', {
              version: pendingVersion.version,
              date: pendingBannerDate,
            })}
          </Alert>
        ) : undefined
      }
      actionError={actionError}
    />
  )
}

export default QualityTestEditor
